<script lang="ts">
	import { onMount } from 'svelte';
	import { get } from 'svelte/store';
	import { createEventDispatcher } from 'svelte';
	//Formating
	import { format } from 'svelte-i18n';
	//Design system components
	import { Button, Text } from '@Shelly/shelly-design-system';
	//Helpers
	import SsidForm from '../DevicesSettings/NetworkComponents/SSIDForm.svelte';
	import NetworkConnectedStatus from '../DevicesSettings/NetworkComponents/NetworkConnectedStatus.svelte';
	import BleWebWiFiScan from './BleWebWiFiScan.svelte';
	import WebBluetoothClient from 'src/helpers/web_bluetooth/web_bluetooth_client';
	import { saved_wifi_neworks } from 'src/stores/features/wifi/passwords';
	import { capFirstChar } from 'src/helpers/utilities';
	import { user_data } from 'src/stores/account/user';
	//Types
	import type { DeviceSettingsG2 } from 'src/types/DeviceSettings';
	import type { WiFiScanRequest } from 'src/types/requests/settings/networks/wifi_request';
	import type { DeviceStatusG2 } from 'src/types/DeviceStatusG2';
	import WebBluetoothEvents from 'src/helpers/web_bluetooth/web_bluetooth_events';
	import type { WebBLEEvent } from 'src/helpers/web_bluetooth/web_bluetooth_types';

	export let client: WebBluetoothClient;
	export let settings: DeviceSettingsG2;
	export let status: DeviceStatusG2;

	let wifiScanResult: WiFiScanRequest[];
	let ip: string = '';
	let ssid: string = '';
	let connectedSSID: string = '';
	let pass: string = '';
	let ssidForm: SsidForm;
	let is_open = false;
	let showForm = false;
	let savePass = false;

	const dispatcher = createEventDispatcher();

	onMount(() => {
		client.addEventListener(WebBluetoothEvents.DATA, onRPCData);
		const savedWifiNetworks = get(saved_wifi_neworks);
		const userEmail = get(user_data).email;
		if (savedWifiNetworks[userEmail]) {
			ssid = savedWifiNetworks?.[userEmail]?.ssid || '';
			pass = savedWifiNetworks?.[userEmail]?.pass || '';
			if (pass) savePass = true;
		}
		ip = status.wifi.sta_ip || '';
		connectedSSID = status.wifi.ssid || '';
		if (connectedSSID) {
			if (ssid && ssid != connectedSSID) {
				pass = '';
				savePass = false;
			}
			ssid = connectedSSID;
		}
		return () => {
			client.removeEventListener(WebBluetoothEvents.DATA, onRPCData);
		};
	});

	const onRPCData = async (e: WebBLEEvent) => {
		const data = e.detail.data;
		if (data.error) {
			if (data.method == 'Wifi.Scan') {
				wifiScanResult = [];
			}
			return;
		}
		switch (data.method) {
			case 'Wifi.Scan':
				wifiScanResult = data?.result?.results;
				break;
		}
	};

	export const validateWifiSetting = (): DeviceSettingsG2['wifi']['sta'] | false | true => {
		if (!ssidForm) {
			//Skip Wifi.SetConfig if the device is already connected.
			return true;
		}
		const wifiSetting: DeviceSettingsG2['wifi']['sta'] | false = ssidForm.validate();
		if (wifiSetting) {
			if (settings.wifi?.sta?.enable === false) {
				wifiSetting.enable = true;
			}
			saved_wifi_neworks.update(get(user_data).email, {
				ssid: wifiSetting.ssid,
				pass: wifiSetting.pass,
			});
		}
		return wifiSetting;
	};

	$: if (ssid || pass) {
		dispatcher('change');
	}
</script>

<Text element="h2" class="mb-s">
	{$format('include_wifi_credentials', {
		default: 'Wi-Fi Credentials',
	})}
</Text>
{#if ip && status.wifi.status == 'got ip'}
	<NetworkConnectedStatus connected={true} {ip} ssid={connectedSSID} />
	{#if !showForm}
		<Button
			size="m"
			class="width-full mr-s"
			responsiveSize={{
				sm: 'm',
			}}
			variant="secondary"
			on:click={() => (showForm = true)}
		>
			{capFirstChar($format('button_change', { default: 'change' }))}
		</Button>
	{/if}
{/if}
{#if !(ip && status.wifi.status == 'got ip') || showForm}
	<Text class="mb-xs mt-m" styleType="body-small">
		{$format('inclusion_wifi_credentials_description', {
			default:
				'Please enter your personal Wi-Fi network credentials. The device will be included in this network.',
		})}
	</Text>
	<SsidForm
		bind:this={ssidForm}
		bind:is_open
		bind:ssid
		bind:pass
		bind:savePass
		save_pass_enabled={true}
	/>
	{#if client.supportNotifications}
		<BleWebWiFiScan
			{wifiScanResult}
			on:click={() => client.call('Wifi.Scan')}
			on:select={(e) => {
				ssidForm.reset();
				ssid = e.detail['value'];
				is_open = e.detail['is_open'];
			}}
		/>
	{/if}
{/if}
